// Centralized API client with auth token handling.
// Uses both HTTP-only cookie (set by backend) AND localStorage Bearer token
// to be resilient against cross-origin cookie restrictions (Safari ITP,
// third-party cookie blocks, etc.).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lh_session_token";

export const setSessionToken = (token) => {
  if (token) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      // localStorage unavailable (private mode etc.) — cookie will be the fallback
    }
  }
};

export const getSessionToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

export const clearSessionToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // ignore
  }
};

// Wrapper around fetch that injects the Bearer token (if present) and
// always sends cookies via `credentials: "include"`.
export const apiFetch = async (path, options = {}) => {
  const url = path.startsWith("http") ? path : `${API}${path}`;
  const token = getSessionToken();

  const headers = new Headers(options.headers || {});
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (options.body && !headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });
};

// Monkey-patch the global `fetch` so that ALL calls to our backend automatically
// include the Bearer token from localStorage. This is the resilient fallback
// when cross-origin cookies are blocked by the browser. Existing page code
// that calls `fetch(${API}/...)` keeps working without modification.
if (typeof window !== "undefined" && !window.__lh_fetch_patched) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    try {
      const url = typeof input === "string" ? input : input?.url || "";
      const isBackendCall = BACKEND_URL && url.startsWith(BACKEND_URL);
      if (isBackendCall) {
        const token = getSessionToken();
        const headers = new Headers(init.headers || (typeof input !== "string" ? input.headers : undefined) || {});
        if (token && !headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return originalFetch(input, {
          ...init,
          headers,
          credentials: init.credentials || "include",
        });
      }
    } catch (e) {
      // Fall through to original fetch on any error
    }
    return originalFetch(input, init);
  };
  window.__lh_fetch_patched = true;
}
